/**
 * Ownership of the club SELF_ENTRY session (CLAUDE.md §15 #55).
 *
 * The SELF_ENTRY token lives in the global PLAYER slot, which outlives the USER session that
 * requested it. On a shared phone the next user must never score with it, so we remember which
 * USER session (token + user id) obtained it and revoke it as soon as that USER session ends or
 * changes — whichever page performed the logout.
 */
import { api } from '../../../lib/api';
import { clearSession, getSession, onSessionChange, setSession } from '../../../lib/auth-store';

const KEY = 'pingo.clubs.selfEntry';

export interface SelfEntryOwner {
  /** the SELF_ENTRY PLAYER token */
  token: string;
  userId: string;
  /** USER session token that requested it */
  userToken: string | null;
  tournamentId: string;
}

function read(): SelfEntryOwner | null {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as SelfEntryOwner) : null;
  } catch {
    return null;
  }
}

function write(v: SelfEntryOwner | null): void {
  try {
    if (v) localStorage.setItem(KEY, JSON.stringify(v));
    else localStorage.removeItem(KEY);
  } catch {
    /* storage unavailable: the PLAYER slot is still cleared below */
  }
}

export function selfEntryOwner(): SelfEntryOwner | null {
  return read();
}

/** Store a freshly issued SELF_ENTRY session in the PLAYER slot, owned by `userId`. */
export function rememberSelfEntrySession(s: {
  token: string;
  expiresAt: string;
  userId: string;
  tournamentId: string;
}): void {
  write({
    token: s.token,
    userId: s.userId,
    userToken: getSession('USER')?.token ?? null,
    tournamentId: s.tournamentId,
  });
  setSession({ token: s.token, expiresAt: s.expiresAt, kind: 'PLAYER' });
}

/**
 * Revoke (POST /auth/logout with the PLAYER token) and clear the SELF_ENTRY session, if the
 * PLAYER slot still holds it. A personal-link PLAYER_VIEW session that replaced it is left alone.
 */
export async function revokeSelfEntrySession(): Promise<void> {
  const owner = read();
  write(null);
  if (!owner) return;
  const player = getSession('PLAYER');
  if (!player || player.token !== owner.token) return;
  clearSession('PLAYER');
  try {
    await api('/api/v1/auth/logout', {
      method: 'POST',
      body: {},
      auth: 'none',
      headers: { Authorization: `Bearer ${owner.token}` },
    });
  } catch {
    /* already expired / offline: the token is gone from this device either way */
  }
}

/** True when the stored SELF_ENTRY session does not belong to the current USER session. */
export function selfEntryOrphaned(currentUserToken: string | null): boolean {
  const owner = read();
  return owner !== null && owner.userToken !== currentUserToken;
}

let installed = false;

/** Revoke the SELF_ENTRY session whenever the USER session that owns it ends or changes. */
export function installSelfEntryGuard(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  if (selfEntryOrphaned(getSession('USER')?.token ?? null)) void revokeSelfEntrySession();
  onSessionChange((kind, session) => {
    if (kind !== 'USER') return;
    if (selfEntryOrphaned(session?.token ?? null)) void revokeSelfEntrySession();
  });
}
